import { memo, useEffect, useMemo, useRef } from 'react';
import PropTypes from 'prop-types';

import { Dropdown, FormRow, Label } from 'basic-components';
import { DataLakeReadableConsts } from 'dashboard-services/api/dataLake';

import useApprovalHandlersModule from '../hooks/useApprovalHandlers'
import useGroupConfigHandlersModule from '../hooks/useGroupConfigHandlers'
import ApprovalSection from './ApprovalSection';
import ToggleRow from './ToggleRow';
import ValidationSection from './ValidationSection';

import { When } from 'react-if';

import './Advanced.scss';

const GROUP_PRIORITY_VALUES = DataLakeReadableConsts.GROUP_PRIORITY_VALUES
const { useApprovalHandlers } = useApprovalHandlersModule
const { useGroupConfigHandlers } = useGroupConfigHandlersModule

const Advanced = ({ dynamicValidation, errors, groupConfig, isLoading = false, menuPortalTarget = undefined, onChangePriority, onChangeValidationOptions, priority, setDynamicValidation, setGroupConfig, validationOptions, validatorTask }) => {
  const { approval, isValidationEnabled = false } = groupConfig,
        defaultPriority = useMemo(() => GROUP_PRIORITY_VALUES.find(option => option.value === priority), [priority]),
        priorityRef = useRef(),
        { onChangeGroupConfig } = useGroupConfigHandlers({ setGroupConfig }),
        { onChangeApproval } = useApprovalHandlers({ setGroupConfig })

  useEffect(() => {
    errors.some(error => error.field === "priority") && priorityRef.current?.validateForForm()
  }, [errors])

  return (
    <div className="data-lake__group-dialog__advanced">
      <FormRow isVertical>
        <Label required>Priority</Label>
        <Dropdown
            defaultValue={defaultPriority}
            disabled={isLoading}
            menuPortalTarget={menuPortalTarget}
            name="priority"
            onChange={onChangePriority}
            options={GROUP_PRIORITY_VALUES}
            ref={priorityRef}
            required
            variant="border"
        />
      </FormRow>
      <ToggleRow
          className="data-lake__group-dialog__advanced__file-validation"
          disabled={isLoading}
          label="File validation"
          name="isValidationEnabled"
          on={isValidationEnabled}
          onChange={onChangeGroupConfig}
          text="Validation configuration for new files and updates"
      />
      <When condition={isValidationEnabled && !isLoading}>
        {() => (
          <ValidationSection
              dynamicValidation={dynamicValidation}
              errors={errors}
              groupConfig={groupConfig}
              menuPortalTarget={menuPortalTarget}
              onChangeValidationOptions={onChangeValidationOptions}
              setDynamicValidation={setDynamicValidation}
              setGroupConfig={setGroupConfig}
              validationOptions={validationOptions}
              validatorTask={validatorTask}
          />
        )}
      </When>
      <ToggleRow
          disabled={isLoading}
          label="File approval"
          name="isApprovalEnabled"
          on={!!approval?.isApprovalEnabled}
          onChange={onChangeApproval}
          text="Approval process configuration for new files and updates"
      />
      <When condition={!!approval?.isApprovalEnabled && !isLoading}>
        {() => (
          <ApprovalSection
              approval={approval}
              errors={errors}
              menuPortalTarget={menuPortalTarget}
              setGroupConfig={setGroupConfig}
          />
        )}
      </When>
    </div>
  )
}

Advanced.propTypes = {
  dynamicValidation: PropTypes.object,
  errors: PropTypes.array.isRequired,
  groupConfig: PropTypes.object.isRequired,
  isLoading: PropTypes.bool,
  menuPortalTarget: PropTypes.object,
  onChangePriority: PropTypes.func.isRequired,
  onChangeValidationOptions: PropTypes.func.isRequired,
  priority: PropTypes.string,
  setDynamicValidation: PropTypes.func.isRequired,
  setGroupConfig: PropTypes.func.isRequired,
  validationOptions: PropTypes.shape({ isRestrictFileTypesOn: PropTypes.bool, shouldRemoveInvalidParams: PropTypes.bool }).isRequired,
  validatorTask: PropTypes.object.isRequired
}

export default memo(Advanced)
